import { useState } from 'react';
import {
  Button,
  Grid,
  TextField,
  Tooltip,
  Box,
  InputAdornment,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import RetailerLookupButton from '../retailerLookupButton.js';
import axios from 'axios';

const AntillesCreditLogForm = ({
  translations,
  user,
  handleFormSelectionReset,
  setSnackbarMessage,
  setIsSnackbarOpen,
  setSnackbarSeverity,
}) => {
  const [form, setForm] = useState({
    jurisdiction: 'Antilles',
    retailerId: '',
    retailerName: '',
    address: '',
    phoneNumber: '',
    logNumber: '',
    dateTicket: null,
    originalTicket: '',
    reprintSalesNumber: '',
    gamePlayed: '',
    amount: '',
    comments: '',
    email: user.email,
    emailAddress: user.email,
    displayName: user.displayName,
    userId: user.userId,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });
  };

  const handleDateTicketChange = value => {
    setForm({
      ...form,
      dateTicket: value,
    });
  };

  const handleClear = () => {
    setForm({
      ...form,
      retailerId: '',
      retailerName: '',
      address: '',
      phoneNumber: '',
      logNumber: '',
      dateTicket: null,
      originalTicket: '',
      reprintSalesNumber: '',
      gamePlayed: '',
      amount: '',
      comments: '',
    });
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await axios({
        method: 'POST',
        url: `${process.env.REACT_APP_ENDPOINT_FORMS}/uscc/antillesCreditLog/submit`,
        data: {
          ...form,
        },
      });
      setSnackbarMessage('Form submitted successfully!');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleClear();
      handleFormSelectionReset();
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbarMessage(
        'There was an error submitting the form. Please try again. If the problem persists, contact support.'
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const {
      retailerId,
      callerName,
      logNumber,
      dateTicket,
      originalTicket,
      gamePlayed,
      amount,
      comments,
    } = form;

    if (
      !retailerId ||
      !callerName ||
      !logNumber ||
      !dateTicket ||
      !originalTicket ||
      !gamePlayed ||
      !amount ||
      !comments
    ) {
      return false;
    }

    return true;
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{
          display: 'flex',
          justifyItems: 'center',
          width: { xs: 'auto', md: 'var(--forms-layout-width, 90vw)' },
        }}
      >
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label='Jurisdiction'
            name='jurisdiction'
            disabled
            value={form.jurisdiction}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            label='Retailer Name'
            name='retailerName'
            value={form.retailerName}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            value={form.phoneNumber}
            onChange={handleChange}
            label='Phone Number'
            name='phoneNumber'
          />
          <TextField
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            value={form.logNumber}
            onChange={handleChange}
            required
            label='Log Number'
            name='logNumber'
          />
          <TextField
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            value={form.originalTicket}
            required
            onChange={handleChange}
            label='Original Ticket'
            name='originalTicket'
          />
          <TextField
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            value={form.gamePlayed}
            required
            onChange={handleChange}
            label='Game Played'
            name='gamePlayed'
          />
          <TextField
            label='Amount'
            required
            name='amount'
            fullWidth
            value={form.amount}
            onChange={handleChange}
            sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
            type='number'
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position='start'>$</InputAdornment>
                ),
              },
            }}
          />
          <TextField
            label='Associate Name'
            disabled
            value={`${user.firstName} ${user.lastName}`}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Tooltip
            title={'Enter a Retailer ID (numeric values only) to lookup'}
            arrow
          >
            <TextField
              label='Retailer ID'
              required
              name='retailerId'
              fullWidth
              value={form.retailerId}
              onChange={handleChange}
              sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
              type='number'
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <RetailerLookupButton form={form} setForm={setForm} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Tooltip>
          <TextField
            label='Address'
            name='address'
            value={form.address}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            required
            label='Caller Name'
            name='callerName'
            fullWidth
            value={form.callerName}
            onChange={handleChange}
            sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label='Date Ticket*'
              required
              value={form.dateTicket}
              onChange={value => handleDateTicketChange(value)}
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
            />
          </LocalizationProvider>
          <TextField
            label='Reprint Sales Number'
            name='reprintSalesNumber'
            fullWidth
            value={form.reprintSalesNumber}
            onChange={handleChange}
            sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
          />
          <TextField
            label='Comments'
            name='comments'
            required
            value={form.comments}
            onChange={handleChange}
            multiline
            rows={4.1}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </Grid>
        <Grid
          size={12}
          sx={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant='contained'
            sx={{
              backgroundColor: 'var(--aldebaran-yellow)',
              color: '#000000',
              fontFamily: 'BrightstarSans-Regular',
              width: '10%',
            }}
            onClick={handleClear}
          >
            Clear
          </Button>
          <Tooltip
            title={
              !isFormValid()
                ? 'Please fill in all required fields to enable the Submit button'
                : ''
            }
            arrow
          >
            <Box>
              <Button
                disabled={!isFormValid() || isSubmitting}
                variant='contained'
                sx={{
                  backgroundColor: 'var(--vega-blue)',
                  fontFamily: 'BrightstarSans-Regular',
                  width: '100%',
                }}
                onClick={handleSubmit}
                loading={isSubmitting}
              >
                {'Submit'}
              </Button>
            </Box>
          </Tooltip>
        </Grid>
      </Grid>
    </>
  );
};

export default AntillesCreditLogForm;
